//
// Base class for DataFormatterType
//

import { DataTools } from "../utils/DataTools";

/**
 * Options that can be configured to help the formatter produce the right data
 * @category Data Formatting
 */
export interface DataFormatterOptions
{
    /** An optional format hint text.  When used with numeric types it should be a Numeral.js format */
    format?: string;

    /** Display unit */
    displayunit?: string;

    // Unit for the original data
    convertfrom?: string;

    // Unit for output data
    convertto?: string
};


/**
 * Base abstract class that defines all the data formatting options available on a per data type.
 * @category Data Formatting
 */
export abstract class DataFormatterType
{
    // Returns the name of the formatter
    abstract getName(): string;

    /*
    Initialize an empty set of options that
    define this class.
    */
    abstract initializeOptions(): DataFormatterOptions;

    /*
    funtion to get the formatted data from the datatype

    @param [Object] data data to be formatted
    @param [Object] options additional options to be used in the format for ex. options for select
    @param [String] path the path where the formatted data needs to be returned
    @return null
    */
    abstract format(data: any, options?: DataFormatterOptions | string, refObj?: any): string;

    /*
    funtion to get the formatted data specific to export

    @param [Object] data data to be formatted
    @param [Object] options additional options to be used in the format for ex. options for select
    @param [String] path the path where the formatted data needs to be returned
    @return null
    */
    abstract formatExport(data: any, options?: DataFormatterOptions, refObj?: any): any

    // Determines if the data passed in is valid for the field type.
    //
    // @param [Object] data data to be considered
    // @param [Object] options additional options to be used in the format for ex. options for select
    // @param [Object] reference object
    // @return null
    //
    validate(data: any, options?: DataFormatterOptions, refObj?: any): DataFormatterType.ValidationError | undefined
    {
        return undefined;
    }

    // funtion to remove formating applied and get raw data
    //
    // @param [Object] data data to be unformatted
    // @param [String] path the path where the formatted data needs to be returned
    // @return null
    //
    abstract unformat(data: any, refObj?: any): any

    // Return unit text to appear to the right of the value
    //
    abstract getUnitText(options?: DataFormatterOptions): string

    // Text alignment default
    abstract getAlign() : string;

    /*
    Helper function that takes an options object or string and
    returns a fully initialized set of objects based on the default
    values returned in initializeOptions

    @param newOptions [DataFormatterOptions | string | null] see DataTools.getOptions for help on formats
    @return [DataFormatterOptions] The resulting merged option set
    */
    public getOptions(newOptions: DataFormatterOptions | string | null | undefined): DataFormatterOptions
    {
        let options = this.initializeOptions();
        if (!newOptions) return options;

        if (typeof newOptions === "string")
        {
            let tempOptions = DataTools.getOptions(newOptions);
            if (Array.isArray(tempOptions))
            {
                if (tempOptions.length === 0) return options;

                // Default first value is the format option
                options.format = tempOptions.shift();

                // Warning if too many values returned without a placement
                if (tempOptions.length !== 0)
                {
                    console.log("Warning in DataFormatterTypes.getOptions, unknown multiple options format: ", newOptions);
                    return options;
                }
            }

            Object.assign<DataFormatterOptions, any>(options, tempOptions);
            return options;
        }

        Object.assign<DataFormatterOptions, any>(options, newOptions);
        return options;
    }

}

export namespace DataFormatterType
{
    export interface ValidationError
    {
        error_type : 'Error' | 'Warning';
        error_message : string;
    };
}


export default DataFormatterType;
